// @djsem/ui — Music Library UX model (Canonical UI Prompt 83).
//
// Pure logic backing the Library screens: track table filter/sort, library-health issue
// derivation, playlist-import report shaping, and smart-crate preview against a track set.

/** Rights/provenance state of a library item, as surfaced to the UI. */
export type DJSEMRightsStateUI = "unknown" | "owned" | "licensed" | "restricted" | "ai_generated_uncleared";

/** A row in the Library track table. */
export interface LibraryTrackRow {
  id: string;
  title: string;
  artist: string;
  bpm?: number;
  key?: string;
  durationSec?: number;
  genre?: string;
  energy?: number;
  mood?: string;
  analysisStatus: "none" | "pending" | "ok" | "failed";
  lyricsStatus: "none" | "pending" | "ok";
  stemStatus: "none" | "pending" | "ok";
  source: "local" | "spotify" | "soundcloud" | "rekordbox";
  platformRefs?: string[];
  rightsState: DJSEMRightsStateUI;
  lastUsedAt?: string;
  warnings?: string[];
  fileExists?: boolean;
  contentHash?: string;
}

// ---- Track table sort/filter (prompt 83 §4) ----

/** Sortable column keys for the track table. */
export type TrackSortKey = "title" | "artist" | "bpm" | "key" | "durationSec" | "energy" | "lastUsedAt";
/** Filterable field names for the track table. */
export type TrackFilterField = "genre" | "analysisStatus" | "rightsState" | "source";

/** Search/sort/filter query for the track table. */
export interface TrackTableQuery {
  search?: string;
  sortKey?: TrackSortKey;
  sortDir?: "asc" | "desc";
  filters?: Partial<Record<TrackFilterField, string>>;
}

/**
 * Apply search, field filters, and sorting to library rows.
 * @param rows - Rows to query.
 * @param q - The search/sort/filter criteria.
 * @returns The filtered and sorted rows.
 */
export function queryTracks(rows: LibraryTrackRow[], q: TrackTableQuery): LibraryTrackRow[] {
  const search = q.search?.trim().toLowerCase() ?? "";
  const filters = q.filters ?? {};
  const dir = q.sortDir === "desc" ? -1 : 1;
  const key = q.sortKey ?? "title";
  return rows
    .filter((r) => !search || [r.title, r.artist, r.genre, r.mood].some((v) => v?.toLowerCase().includes(search)))
    .filter((r) => (Object.keys(filters) as TrackFilterField[]).every((f) => !filters[f] || String(r[f] ?? "") === filters[f]))
    .slice()
    .sort((a, b) => {
      const av = a[key], bv = b[key];
      if (av === bv) return 0;
      if (av === undefined) return 1;
      if (bv === undefined) return -1;
      return av > bv ? dir : -dir;
    });
}

// ---- Library Health (prompt 83 §8) ----

/** Category of a detected library health issue. */
export type LibraryHealthKind =
  | "missing_file" | "duplicate" | "bad_metadata" | "low_analysis_confidence"
  | "missing_bpm_key" | "missing_lyrics" | "missing_rights_declaration"
  | "unmatched_platform_track" | "unsupported_file" | "large_storage_item";

/** A single detected issue on a library track, with a suggested fix action. */
export interface LibraryHealthIssue {
  trackId: string;
  kind: LibraryHealthKind;
  detail: string;
  suggestedAction: "fix_metadata" | "merge_duplicates" | "locate_missing_file" | "mark_ignored" | "run_analysis" | "create_cleanup_task" | "declare_rights";
}

/** Full library-health report: all issues, counts by kind, and an overall score. */
export interface LibraryHealthReport {
  issues: LibraryHealthIssue[];
  countsByKind: Record<LibraryHealthKind, number>;
  healthScore: number; // 0..1
}

/**
 * Scan library rows for health issues (missing files, duplicates, bad metadata, etc.).
 * @param rows - Rows to analyse.
 * @returns The full {@link LibraryHealthReport}.
 */
export function analyseLibraryHealth(rows: LibraryTrackRow[]): LibraryHealthReport {
  const issues: LibraryHealthIssue[] = [];
  const seen = new Map<string, string>();
  for (const r of rows) {
    if (r.fileExists === false) issues.push({ trackId: r.id, kind: "missing_file", detail: `${r.title} — file not found`, suggestedAction: "locate_missing_file" });
    if (r.contentHash) {
      const other = seen.get(r.contentHash);
      if (other) issues.push({ trackId: r.id, kind: "duplicate", detail: `Duplicate of ${other}`, suggestedAction: "merge_duplicates" });
      else seen.set(r.contentHash, r.id);
    }
    if (!r.title || !r.artist) issues.push({ trackId: r.id, kind: "bad_metadata", detail: "Missing title or artist", suggestedAction: "fix_metadata" });
    if (r.bpm === undefined || r.key === undefined) issues.push({ trackId: r.id, kind: "missing_bpm_key", detail: "BPM or key missing", suggestedAction: "run_analysis" });
    if (r.rightsState === "unknown") issues.push({ trackId: r.id, kind: "missing_rights_declaration", detail: "Rights not declared", suggestedAction: "declare_rights" });
    if ((r.source === "spotify" || r.source === "soundcloud") && !r.contentHash) issues.push({ trackId: r.id, kind: "unmatched_platform_track", detail: "Platform reference has no matched local audio", suggestedAction: "locate_missing_file" });
  }
  const countsByKind = {} as Record<LibraryHealthKind, number>;
  for (const i of issues) countsByKind[i.kind] = (countsByKind[i.kind] ?? 0) + 1;
  const affected = new Set(issues.map((i) => i.trackId)).size;
  const healthScore = rows.length === 0 ? 1 : Math.max(0, 1 - affected / rows.length);
  return { issues, countsByKind, healthScore };
}

// ---- Playlist Import Report (prompt 83 §7) ----

/** A single incoming playlist item (metadata reference only). */
export interface PlaylistImportItem { title: string; artist: string; platformRef?: string; }
/** An incoming playlist item matched (or not) against the local library. */
export interface PlaylistImportMatch { item: PlaylistImportItem; matchedTrackId?: string; needsAcquisition: boolean; rightsState: DJSEMRightsStateUI; }
/** Summary report for a playlist import: match rates and the required metadata-only warning. */
export interface PlaylistImportReport {
  totalIncoming: number;
  matchedCount: number;
  needsAcquisitionCount: number;
  restrictedCount: number;
  metadataOnlyWarning: string;
  matches: PlaylistImportMatch[];
}

/**
 * Match incoming playlist items against the local library by title+artist and build the import report.
 * @param items - Incoming playlist items.
 * @param library - Local library rows to match against.
 * @returns The {@link PlaylistImportReport}, including the mandatory metadata-only warning.
 */
export function buildPlaylistImportReport(items: PlaylistImportItem[], library: LibraryTrackRow[]): PlaylistImportReport {
  const norm = (s: string) => s.trim().toLowerCase();
  const matches: PlaylistImportMatch[] = items.map((it) => {
    const found = library.find((r) => norm(r.title) === norm(it.title) && norm(r.artist) === norm(it.artist));
    return { item: it, matchedTrackId: found?.id, needsAcquisition: !found, rightsState: found?.rightsState ?? "restricted" /* per 65: platform refs are not owned */ };
  });
  return {
    totalIncoming: items.length,
    matchedCount: matches.filter((m) => m.matchedTrackId).length,
    needsAcquisitionCount: matches.filter((m) => m.needsAcquisition).length,
    restrictedCount: matches.filter((m) => m.rightsState === "restricted").length,
    metadataOnlyWarning: "Imported items are metadata references only — platform audio is not downloaded (see prompt 65).",
    matches,
  };
}

// ---- Smart Crate preview (prompt 83 §6) ----

/** Comparison operator for a smart-crate rule. */
export type SmartCrateOp = "eq" | "neq" | "lt" | "gt" | "gte" | "lte" | "contains";
/** A single smart-crate matching rule: field + operator + comparison value. */
export interface SmartCrateRule { field: keyof LibraryTrackRow; op: SmartCrateOp; value: string | number; }
/** A smart-crate definition: its rules and whether all or any must match. */
export interface SmartCrateSpec { rules: SmartCrateRule[]; mode: "all" | "any"; }

/**
 * Preview which rows a smart-crate spec would currently match.
 * @param spec - The smart-crate rule set.
 * @param rows - Rows to test.
 * @returns The matching rows.
 */
export function previewSmartCrate(spec: SmartCrateSpec, rows: LibraryTrackRow[]): LibraryTrackRow[] {
  const matchRule = (r: LibraryTrackRow, rule: SmartCrateRule): boolean => {
    const v = r[rule.field];
    switch (rule.op) {
      case "eq": return v === rule.value;
      case "neq": return v !== rule.value;
      case "lt": return typeof v === "number" && typeof rule.value === "number" && v < rule.value;
      case "gt": return typeof v === "number" && typeof rule.value === "number" && v > rule.value;
      case "lte": return typeof v === "number" && typeof rule.value === "number" && v <= rule.value;
      case "gte": return typeof v === "number" && typeof rule.value === "number" && v >= rule.value;
      case "contains": return typeof v === "string" && typeof rule.value === "string" && v.toLowerCase().includes(rule.value.toLowerCase());
    }
  };
  return rows.filter((r) => (spec.mode === "all" ? spec.rules.every((rr) => matchRule(r, rr)) : spec.rules.some((rr) => matchRule(r, rr))));
}
